import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, ConfirmationNumber, DeleteOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import { Util } from '../../util/Util';
import { MONEY_LIMITS, roundMoney } from '../../util/quantity';
import {
  MoleDialog,
  MoleDialogHeader,
  useMoleThemeColor,
} from '../../components/MoleDialogShell';
import { Z_INDEX } from '../../styles/zIndex';
import { voucherTenderTotal, type VoucherTenderLine } from './voucherTender';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onApply: (lines: VoucherTenderLine[]) => void;
  initialLines?: VoucherTenderLine[];
  maxSpend?: number;
};

const VoucherTenderModal = ({
  isOpen,
  onClose,
  onApply,
  initialLines = [],
  maxSpend,
}: Props) => {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { queueReRun } = useUI();
  const themeColor = useMoleThemeColor();
  const [code, setCode] = useState('');
  const [lines, setLines] = useState<VoucherTenderLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setLines(initialLines.map((line) => ({ ...line })));
    setCode('');
    setError('');
  }, [isOpen]);

  const total = useMemo(() => voucherTenderTotal(lines), [lines]);
  const overSpend = Boolean(maxSpend != null && total > Number(maxSpend));
  const invalidSpend = lines.some(
    (line) => line.spend < 0.01 || line.spend > line.remaining || line.spend > MONEY_LIMITS.max,
  );
  const canApply = lines.length > 0 && !invalidSpend && !overSpend;

  const addCode = async () => {
    const nextCode = code.trim().toUpperCase();
    if (!nextCode) return;
    if (lines.some((line) => Util.compareStrings(line.code, nextCode))) {
      setError(t('common:voucher.tender.alreadyAdded'));
      return;
    }
    setLoading(true);
    setError('');
    const res = await api.get('/vouchers/tender', {
      params: { Code: nextCode },
      skipErrorToast: true,
    } as Parameters<typeof api.get>[1]);
    setLoading(false);
    if (res?.status === 200 && res.data?.id) {
      const remaining = Math.max(0, Number(res.data.remaining ?? res.data.amount ?? 0));
      if (remaining <= 0) {
        setError(t('common:voucher.tender.noBalance'));
        return;
      }
      setLines((prev) => [
        ...prev,
        {
          id: Number(res.data.id),
          code: String(res.data.code || nextCode),
          amount: Number(res.data.amount || 0),
          remaining,
          issued: res.data.issued || null,
          expiry: res.data.expiry || null,
          spend: remaining,
        },
      ]);
      setCode('');
      return;
    }
    if (res?.status === 401 || res?.status === 422) {
      queueReRun(addCode);
      return;
    }
    setError(t('common:voucher.tender.notFound'));
  };

  const updateSpend = (id: number, raw: number) => {
    const value = Number.isFinite(raw) ? Math.max(0, Math.min(MONEY_LIMITS.max, roundMoney(raw))) : 0;
    setLines((prev) =>
      prev.map((line) => (line.id === id ? { ...line, spend: value } : line)),
    );
  };

  const handleApply = () => {
    if (!canApply) return;
    onApply(lines);
    toast.success(t('common:voucher.tender.applied'), { autoClose: 2000 });
    onClose();
  };

  return (
    <MoleDialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      themeColor={themeColor}
      sx={{ zIndex: Z_INDEX.nestedDialog }}
    >
      <MoleDialogHeader
        title={t('common:voucher.tender.title')}
        subtitle={t('common:voucher.tender.hint')}
        icon={<ConfirmationNumber />}
        onClose={onClose}
        themeColor={themeColor}
      />
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField variant="filled"
              fullWidth
              size="small"
              label={t('common:voucher.code')}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void addCode();
                }
              }}
              error={Boolean(error)}
              helperText={error || t('common:voucher.tender.codeHint')}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => void addCode()}
              disabled={loading || !code.trim()}
              sx={{ minWidth: 120, height: 40 }}
            >
              {t('common:ui.buttons.add')}
            </Button>
          </Stack>

          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('common:voucher.code')}</TableCell>
                  <TableCell align="right">{t('common:tables.amount')}</TableCell>
                  <TableCell align="right">{t('common:voucher.tender.balance')}</TableCell>
                  <TableCell>{t('common:voucher.issued')}</TableCell>
                  <TableCell>{t('common:voucher.expiry')}</TableCell>
                  <TableCell align="right">{t('common:voucher.tender.spend')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary">
                        {t('common:voucher.tender.empty')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  lines.map((line) => {
                    const spendError = line.spend < 1 || line.spend > line.remaining;
                    return (
                      <TableRow key={line.id}>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{line.code}</TableCell>
                        <TableCell align="right">{Util.formatNumber(line.amount)}</TableCell>
                        <TableCell align="right">{Util.formatNumber(line.remaining)}</TableCell>
                        <TableCell>{line.issued ? Util.formatDate(line.issued, 'medium') : '—'}</TableCell>
                        <TableCell>{line.expiry ? Util.formatDate(line.expiry, 'medium') : '—'}</TableCell>
                        <TableCell align="right" sx={{ minWidth: 120 }}>
                          <TextField variant="filled"
                            size="small"
                            type="number"
                            value={line.spend}
                            error={spendError}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => updateSpend(line.id, Number(e.target.value))}
                            inputProps={{ min: 0.01, max: Math.min(line.remaining, MONEY_LIMITS.max), step: 0.01, style: { textAlign: 'right' } }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setLines((prev) => prev.filter((item) => item.id !== line.id))}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Box>

          <Typography variant="subtitle2">
            {t('common:voucher.tender.total')}: <b>{Util.formatNumber(total)}</b>
            {overSpend ? (
              <Typography component="span" color="error" sx={{ ml: 1 }}>
                {t('common:voucher.tender.exceedsInvoice', { max: Util.formatNumber(Number(maxSpend || 0)) })}
              </Typography>
            ) : null}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('common:ui.buttons.cancel')}</Button>
        <Button
          variant="outlined"
          onClick={() => {
            onApply([]);
            onClose();
          }}
        >
          {t('common:voucher.tender.clear')}
        </Button>
        <Button variant="contained" disabled={!canApply} onClick={handleApply}>
          {t('common:ui.buttons.confirm')}
        </Button>
      </DialogActions>
    </MoleDialog>
  );
};

export default VoucherTenderModal;
